/**
 * Invoice items catalog (/invoice-items/list).
 *
 * Reusable line items for the invoice form: staff create them ahead of time
 * and pick them when they write an invoice. Rows live in `invoice_items`,
 * scoped to the organization; `event_id` is an optional label.
 */
import { useEffect, useRef, useState } from "react";
import {
  Link,
  LoaderFunctionArgs,
  useLoaderData,
  useNavigate,
  useRevalidator,
} from "react-router-dom";
import moment from "moment";
import Switch from "react-switch";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi, SizeColumnsToContentStrategy } from "ag-grid-community";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { organizationStore } from "../../store/organization";
import { usePermissions } from "../auth/api";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import { Badge } from "../../components/Badge";
import { Input } from "../../components/inputs/Input";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import { InvoiceItem } from "../invoices/type";
import { GET_INVOICE_ITEMS } from "./queries";

const client = createApolloClient();
const { getState: getOrganizationState } = organizationStore;

const buildSearch = (search: string) => [
  { name: { _ilike: `%${search}%` } },
  { description: { _ilike: `%${search}%` } },
];

export const invoiceItemsListLoader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const search = url.searchParams.get("search");
  const rawOrganizationId = getOrganizationState().organizationId;
  const organizationId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : "", 10);

  let where: any = { _or: search ? buildSearch(search) : undefined };
  if (organizationId && organizationId !== 0) {
    where = { ...where, organization_id: { _eq: organizationId } };
  }
  const { data } = await client.query({
    query: GET_INVOICE_ITEMS,
    variables: { where },
    fetchPolicy: "network-only",
  });
  return (data?.invoice_items ?? []) as InvoiceItem[];
};

const money = (v: unknown, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(v) || 0);

export const InvoiceItemList = () => {
  const { editCreatePermission } = usePermissions("invoices");
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const gridRef = useRef<GridApi>();
  const items = useLoaderData() as InvoiceItem[];
  const [rowData, setRowData] = useState<InvoiceItem[]>([]);
  const [isActiveToggle, setIsActiveToggle] = useState(true);

  useEffect(() => {
    setRowData(isActiveToggle ? items.filter((i) => i.is_active) : items);
  }, [items, isActiveToggle]);

  const linkCell = (p: { value: any; data: InvoiceItem }) => {
    if (p.value === undefined || p.value === null) return null;
    return editCreatePermission ? (
      <Link to={`/invoice-items/${p.data.id}/edit`}>
        <Badge>{String(p.value)}</Badge>
      </Link>
    ) : (
      <Badge>{String(p.value)}</Badge>
    );
  };

  const colDefs: ColDef<InvoiceItem>[] = [
    { headerName: "Id", field: "id", cellRenderer: linkCell, filter: "agTextColumnFilter", pinned: "left" },
    { headerName: "Name", field: "name", cellRenderer: linkCell, filter: "agTextColumnFilter", pinned: "left" },
    {
      headerName: "Is Active?",
      field: "is_active",
      valueFormatter: (p) => (p.value ? "Yes" : "No"),
      filter: "agTextColumnFilter",
      filterValueGetter: (p) => (p.data?.is_active ? "Yes" : "No"),
    },
    {
      headerName: "Amount",
      field: "amount",
      valueFormatter: (p) => money(p.value),
      filter: "agTextColumnFilter",
      filterValueGetter: (p) => money(p.data?.amount),
    },
    {
      headerName: "Tax %",
      field: "tax_percentage",
      valueFormatter: (p) => `${Number(p.value ?? 0)}%`,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Service Fee %",
      field: "service_fee_percentage",
      valueFormatter: (p) => `${Number(p.value ?? 0)}%`,
      filter: "agTextColumnFilter",
    },
    { headerName: "Description", field: "description", filter: "agTextColumnFilter" },
    {
      headerName: "Event",
      valueGetter: (p) => p.data?.event?.name ?? "",
      filter: "agTextColumnFilter",
    },
    { headerName: "Sort Order", field: "sort_order", filter: "agTextColumnFilter" },
    { headerName: "Updated By", valueGetter: (p) => p.data?.user?.username ?? "" },
    {
      headerName: "Updated Date",
      field: "updated_at",
      valueFormatter: (p) => (p.value ? moment(p.value).format("MM/DD/YYYY h:mm:ss a") : ""),
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.updated_at ? moment(p.data.updated_at).format("MM/DD/YYYY h:mm:ss a") : "",
    },
  ];

  const autoSizeStrategy: SizeColumnsToContentStrategy = { type: "fitCellContents" };

  const handleExportCSV = () => {
    if (!gridRef.current) return;
    const timestamp = new Date().toISOString().replace(/[-:]/g, "");
    gridRef.current.exportDataAsCsv({ fileName: `InvoiceItems_${timestamp}.csv`, columnSeparator: "," });
  };

  return (
    <div style={{ height: "75vh" }}>
      <div className="ag-theme-quartz" style={{ width: "100%", height: "100%" }}>
        <div className="grid gap-2 grid-cols-6">
          <Breadcrumbs />
          <div className="col-span-1 col-start-4 mt-3 flex flex-col items-center">
            <span className="block text-sm font-medium text-gray-500">Active Only</span>
            <Switch
              checked={isActiveToggle}
              onChange={() => setIsActiveToggle((prev) => !prev)}
              onColor="#EE2E30"
              onHandleColor="#FFFFFF"
              handleDiameter={18}
              uncheckedIcon={false}
              checkedIcon={false}
              boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
              activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
              height={15}
              width={48}
            />
          </div>
          <div className="col-span-2 col-start-5 mt-2">
            <Input
              name="search"
              placeholder="Search Invoice Items"
              onChange={(s) => {
                navigate(
                  s && typeof s === "string" && s.length > 0
                    ? `/invoice-items/list?search=${s}`
                    : `/invoice-items/list`
                );
                revalidator.revalidate();
              }}
            />
          </div>
          <div className="py-2 col-span-2 col-start-7">
            <SecondaryButton onClick={handleExportCSV}>Export to CSV</SecondaryButton>
            {editCreatePermission && (
              <PrimaryButton onClick={() => navigate(`/invoice-items/create`)}>Create</PrimaryButton>
            )}
          </div>
        </div>
        <AgGridReact
          rowData={rowData}
          columnDefs={colDefs}
          rowHeight={50}
          autoSizeStrategy={autoSizeStrategy}
          pagination
          gridOptions={{ enableCellTextSelection: true }}
          paginationPageSize={100}
          onGridReady={(params) => (gridRef.current = params.api)}
        />
      </div>
    </div>
  );
};
